import React, { useEffect, useState } from "react";
import { Building2, CalendarDays, ChevronLeft, ChevronRight, Clock3, Phone, Stethoscope, UserRound } from "lucide-react";
import { formatISTDate, getISTDateInputValue } from "../utils/dateTime";

const APPOINTMENTS_URL = "https://api-gshf6jfmfq-uc.a.run.app/api/somerandomnumberappointments";

const getStatusClass = (status) => {
    const normalized = String(status || "").toUpperCase();

    if (normalized.includes("COMPLET")) return "bg-success";
    if (normalized.includes("IN_PROGRESS")) return "bg-info";
    if (normalized.includes("ASSIGN")) return "bg-primary";
    if (normalized.includes("PEND")) return "bg-warning text-dark";
    if (normalized.includes("SCHED")) return "bg-success scheduled-status-badge";
    if (normalized.includes("BOOK")) return "bg-secondary";
    if (normalized.includes("CANCEL")) return "bg-danger";

    return "bg-dark";
};

const buildWhatsAppUrl = (mobile) => {
    const digits = String(mobile || "").replace(/\D/g, "");
    if (!digits) return "";
    return `https://wa.me/${digits}?text=`;
};

const formatAppointmentTime = (value) => {
    if (!value) return "-";

    const time = String(value).trim();
    const match = time.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (!match) return time;

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) return time;

    const displayHours = hours % 12 || 12;
    return `${displayHours}:${match[2]} ${hours >= 12 ? "PM" : "AM"}`;
};

const getAppointmentTypeIcon = (appointmentType) => {
    switch (String(appointmentType || "").trim().toLowerCase()) {
        case "online":
            return <Phone size={14} />;
        case "clinic":
            return <Building2 size={14} />;
        default:
            return null;
    }
};

function HomeAppointmentsPanel() {
    const [selectedDate, setSelectedDate] = useState(getISTDateInputValue);
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const loadAppointments = async (dateValue) => {
        try {
            setLoading(true);
            setError("");

            const res = await fetch(`${APPOINTMENTS_URL}?date=${dateValue}`);

            if (!res.ok) {
                throw new Error(`Unable to load appointments (${res.status})`);
            }

            const data = await res.json();
            const list = Array.isArray(data)
                ? data
                : Array.isArray(data?.appointments)
                    ? data.appointments
                    : Array.isArray(data?.content)
                        ? data.content
                        : Array.isArray(data?.data)
                            ? data.data
                            : [];

            setAppointments(list);
        } catch (err) {
            console.error(err);
            setAppointments([]);
            setError("Appointments could not be loaded right now. Please check the server.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAppointments(selectedDate);
    }, [selectedDate]);

    const moveDate = (offset) => {
        const [year, month, day] = selectedDate.split("-").map(Number);
        const next = new Date(Date.UTC(year, month - 1, day + offset));
        setSelectedDate(next.toISOString().slice(0, 10));
    };

    const today = getISTDateInputValue();

    return (
        <section className="appointments-panel mb-4">
            <div className="appointments-header">
                <div>
                    <p className="appointments-kicker mb-1">Daily Schedule</p>
                    <h5 className="mb-1">Appointments</h5>

                    {appointments.length > 0 && (
                        <span
                            className="appointment-notification"
                            title={`${appointments.length} appointment${appointments.length === 1 ? "" : "s"} scheduled`}
                            aria-label={`${appointments.length} appointments scheduled`}
                        >
                            <span className="appointment-notification-dot"></span>
                        </span>
                    )}
                    <div className="appointments-date-label">
                        {formatISTDate(selectedDate, {
                            weekday: "long",
                            day: "2-digit",
                            month: "long",
                            year: "numeric"
                        })}
                    </div>
                </div>

                <div className="appointments-controls">
                    <button
                        type="button"
                        className="appointments-nav-btn"
                        onClick={() => moveDate(-1)}
                        aria-label="Previous day"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <div className="appointments-date-wrap">
                        <CalendarDays size={16} className="appointments-calendar-icon" />
                        <input
                            type="date"
                            className="appointments-date-input"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value || today)}
                            aria-label="Select appointment date"
                        />
                    </div>
                    <button
                        type="button"
                        className="appointments-nav-btn"
                        onClick={() => moveDate(1)}
                        aria-label="Next day"
                    >
                        <ChevronRight size={16} />
                    </button>
                    <button
                        type="button"
                        className="appointments-today-btn"
                        onClick={() => setSelectedDate(today)}
                    >
                        Today
                    </button>
                </div>
            </div>

            <div className="appointments-body">
                <div className="appointments-summary mb-3">
                    <CalendarDays size={16} />
                    <span>{appointments.length} appointment{appointments.length === 1 ? "" : "s"} on this day</span>
                </div>

                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border" role="status"></div>
                        <div className="mt-2">Loading appointments...</div>
                    </div>
                ) : error ? (
                    <div className="alert alert-danger mb-0">{error}</div>
                ) : appointments.length === 0 ? (
                    <div className="appointments-empty">
                        No appointments found for this date.
                    </div>
                ) : (
                    <div className="appointments-list">
                        {appointments.map((appointment, index) => (
                            <article className="appointment-card" key={appointment.id || `${selectedDate}-${index}`}>
                                {String(appointment.appointmentType || "").trim() && (
                                    <div className="appointment-meta mb-2">
                                        <span className="d-inline-flex align-items-center gap-1">
                                            {getAppointmentTypeIcon(appointment.appointmentType)}
                                            {appointment.appointmentType}
                                        </span>
                                    </div>
                                )}
                                <div className="appointment-card-top">
                                    <div className="appointment-time">
                                        <Clock3 size={16} />
                                        <span>{formatAppointmentTime(appointment.appointmentTime || appointment.timeSlot)}</span>
                                    </div>
                                    <span className={`badge ${getStatusClass(appointment.status)}`}>
                                        {appointment.status || "UNKNOWN"}
                                    </span>
                                </div>

                                <div className="appointment-pet">
                                    <h6 className="mb-1">{appointment.petName || "Unnamed pet"} ({appointment.petSpecies})</h6>
                                    <div className="appointment-meta">
                                        {String(appointment.mobileNumber || appointment.mobile || "").trim() ? (
                                            <a
                                                href={buildWhatsAppUrl(appointment.mobileNumber || appointment.mobile)}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="d-inline-flex align-items-center gap-1 text-decoration-none"
                                                title="Open WhatsApp chat"
                                            >
                                                <UserRound size={14} />
                                                <span>{appointment.mobileNumber || appointment.mobile}</span>
                                            </a>
                                        ) : (
                                            <span className="d-inline-flex align-items-center gap-1">
                                                
                                                <UserRound size={14} />
                                                {appointment.ownerName || "Owner not listed"}
                                            </span>
                                        )}
                                        {String(appointment.doctorName || "").trim() ? (
                                            <span className="d-inline-flex align-items-center gap-1">
                                                <Stethoscope size={14} />
                                                {appointment.doctorName}
                                            </span>
                                        ) : null}
                                    </div>
                                </div>

                                <p className="appointment-reason mb-0" title={appointment.appointmentReason || appointment.reason}>
                                    {appointment.appointmentReason || appointment.reason || "No reason provided"}
                                </p>
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

export default HomeAppointmentsPanel;
